import React, { useState, useEffect, useMemo } from 'react';
import { getCouvertureErdv, saveCouvertureErdv } from '../services/api';

/**
 * Secteurs techniciens — la couverture qui pilote l'ouverture des créneaux
 * E-RDV chez Covage et la recommandation de rendez-vous.
 *
 * Le rang ordonne les départements d'un technicien, 1 étant sa zone
 * principale. Les codes postaux ne se remplissent que si la couverture est
 * partielle : le Var partagé entre deux techniciens, ou Paris découpé par
 * arrondissements.
 */

const MODE_LIBELLE = {
  auto:     { texte: 'Automatique', aide: 'Créneaux ouverts tous les jours ouvrés' },
  rotation: { texte: 'Rotation',    aide: 'Ouvert seulement aux jours du cycle, pour grouper les déplacements' },
  manuel:   { texte: 'Manuel',      aide: 'Jamais touché automatiquement' },
};

const JOURS = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'];

function Pastille({ ton, children }) {
  const tons = {
    vert:   { bg: 'rgba(46,160,67,.15)',  fg: 'var(--green2)' },
    orange: { bg: 'rgba(219,139,0,.18)',  fg: '#d98a00' },
    gris:   { bg: 'var(--border)',        fg: 'var(--text3)' },
    rouge:  { bg: 'rgba(255,107,107,.15)', fg: '#ff6b6b' },
  }[ton] || {};
  return (
    <span style={{
      background: tons.bg, color: tons.fg, fontSize: 11, fontWeight: 700,
      padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

export default function Secteurs() {
  const [etat, setEtat]       = useState(null);
  const [connus, setConnus]   = useState([]);
  const [chargement, setChargement] = useState(true);
  const [sauvegarde, setSauvegarde] = useState(false);
  const [ok, setOk]           = useState(false);
  const [erreurs, setErreurs] = useState([]);
  const [erreur, setErreur]   = useState(null);
  const [filtre, setFiltre]   = useState('');
  const [nouveau, setNouveau] = useState('');
  // Le champ des codes postaux garde sa frappe brute le temps de la saisie.
  // Sans cela, chaque touche renvoyait la liste reformatée et l'espace de
  // séparation disparaissait : impossible de taper un second code.
  const [brouillons, setBrouillons] = useState({});

  useEffect(() => {
    setChargement(true);
    getCouvertureErdv()
      .then(d => {
        setEtat({ techniciens: d.techniciens || {}, departements: d.departements || {}, rotations: d.rotations || [], exclus: d.exclus || [], maj: d.maj });
        setConnus(d.techniciens_connus || []);
      })
      .catch(() => setErreur('Chargement impossible'))
      .finally(() => setChargement(false));
  }, []);

  const nomDe = zoho => (connus.find(c => c.zoho_nom === zoho) || {}).nom || '';

  // Un département listé par un technicien mais absent de la table des modes
  // n'est piloté par rien : on le signale plutôt que de le laisser muet.
  const deptsSansMode = useMemo(() => {
    if (!etat) return [];
    const vus = new Set();
    Object.values(etat.techniciens).forEach(t => (t.zones || []).forEach(z => vus.add(String(z.dept))));
    return [...vus].filter(d => !etat.departements[d]).sort();
  }, [etat]);

  const deptsSansTech = useMemo(() => {
    if (!etat) return [];
    const couverts = new Set();
    Object.values(etat.techniciens).forEach(t => (t.zones || []).forEach(z => couverts.add(String(z.dept))));
    return Object.entries(etat.departements)
      .filter(([d, m]) => m !== 'manuel' && !couverts.has(d))
      .map(([d]) => d).sort();
  }, [etat]);

  // ── Modifications ──────────────────────────────────────────────────────────
  const majTech = (tech, zones) =>
    setEtat(e => ({ ...e, techniciens: { ...e.techniciens, [tech]: { ...e.techniciens[tech], zones } } }));

  const ajouterZone = tech => {
    const zones = [...(etat.techniciens[tech].zones || [])];
    zones.push({ dept: '', rang: zones.length + 1, codes_postaux: [] });
    setBrouillons({});
    majTech(tech, zones);
  };

  const modifierZone = (tech, i, champ, valeur) => {
    const zones = (etat.techniciens[tech].zones || []).map((z, j) => {
      if (j !== i) return z;
      if (champ === 'codes_postaux') {
        return { ...z, codes_postaux: valeur.split(/[^0-9]+/).filter(Boolean) };
      }
      if (champ === 'rang') return { ...z, rang: parseInt(valeur, 10) || 1 };
      return { ...z, dept: String(valeur).replace(/[^0-9AB]/gi, '').slice(0, 2) };
    });
    majTech(tech, zones);
  };

  const supprimerZone = (tech, i) => {
    setBrouillons({});
    majTech(tech, (etat.techniciens[tech].zones || []).filter((_, j) => j !== i));
  };

  const deplacerZone = (tech, i, delta) => {
    const zones = [...(etat.techniciens[tech].zones || [])];
    const j = i + delta;
    if (j < 0 || j >= zones.length) return;
    [zones[i], zones[j]] = [zones[j], zones[i]];
    setBrouillons({});
    majTech(tech, zones.map((z, k) => ({ ...z, rang: k + 1 })));
  };

  // Retirer ne touche pas au compte du technicien, qui se gere depuis la page
  // Techniciens : cela le sort seulement de la couverture. Un technicien connu
  // de l'application doit etre memorise comme retire, sans quoi il reviendrait
  // au rechargement suivant.
  const retirerTech = tech => {
    const zones = etat.techniciens[tech]?.zones || [];
    if (zones.length && !window.confirm(
      `Retirer ${tech} de la couverture ?

Ses ${zones.length} département(s) seront libérés. ` +
      `Son compte n'est pas supprimé : il reste dans la page Techniciens.`)) return;
    setEtat(e => {
      const t = { ...e.techniciens };
      delete t[tech];
      const connu = connus.some(c => c.zoho_nom === tech);
      return {
        ...e,
        techniciens: t,
        exclus: connu ? [...new Set([...(e.exclus || []), tech])] : (e.exclus || []),
        rotations: (e.rotations || []).map(r => r.technicien === tech ? { ...r, technicien: '' } : r),
      };
    });
  };

  const reintegrerTech = tech =>
    setEtat(e => ({
      ...e,
      exclus: (e.exclus || []).filter(x => x !== tech),
      techniciens: { ...e.techniciens, [tech]: e.techniciens[tech] || { zones: [] } },
    }));

  const ajouterTech = () => {
    const nom = nouveau.trim();
    if (!nom) return;
    if (etat.techniciens[nom]) { setNouveau(''); return; }
    setEtat(e => ({
      ...e,
      exclus: (e.exclus || []).filter(x => x !== nom),
      techniciens: { ...e.techniciens, [nom]: { zones: [] } },
    }));
    setNouveau('');
  };

  const setMode = (dept, mode) =>
    setEtat(e => ({ ...e, departements: { ...e.departements, [dept]: mode } }));

  const majRotation = (i, champ, valeur) =>
    setEtat(e => ({
      ...e,
      rotations: e.rotations.map((r, j) => j !== i ? r : {
        ...r,
        [champ]: ['cycle', 'position'].includes(champ) ? (parseInt(valeur, 10) || 0) : valeur,
      }),
    }));

  const ajouterRotation = () =>
    setEtat(e => ({ ...e, rotations: [...e.rotations, { dept: '', technicien: '', jour: 'vendredi', cycle: 3, position: 0 }] }));

  const supprimerRotation = i =>
    setEtat(e => ({ ...e, rotations: e.rotations.filter((_, j) => j !== i) }));

  const enregistrer = async () => {
    setSauvegarde(true); setErreurs([]); setErreur(null);
    try {
      const r = await saveCouvertureErdv(etat);
      setEtat(x => ({ ...x, maj: r.maj }));
      setOk(true); setTimeout(() => setOk(false), 2500);
    } catch (e) {
      const d = e.response?.data;
      if (d?.erreurs) setErreurs(d.erreurs);
      else setErreur(d?.error || 'Enregistrement impossible');
    } finally { setSauvegarde(false); }
  };

  if (chargement) return (
    <>
      <div className="topbar"><div className="topbar-title">Secteurs techniciens</div></div>
      <div className="page"><div style={{ color: 'var(--text3)', padding: 40, textAlign: 'center' }}>Chargement…</div></div>
    </>
  );

  if (!etat) return (
    <>
      <div className="topbar"><div className="topbar-title">Secteurs techniciens</div></div>
      <div className="page"><div style={{ color: '#ff6b6b', padding: 40, textAlign: 'center' }}>{erreur}</div></div>
    </>
  );

  const techs = Object.keys(etat.techniciens)
    .sort((a, b) => a.localeCompare(b, 'fr', { numeric: true }))
    .filter(t => !filtre || t.toLowerCase().includes(filtre.toLowerCase())
              || nomDe(t).toLowerCase().includes(filtre.toLowerCase())
              || (etat.techniciens[t].zones || []).some(z => String(z.dept).includes(filtre)));

  const tousDepts = [...new Set([
    ...Object.keys(etat.departements),
    ...Object.values(etat.techniciens).flatMap(t => (t.zones || []).map(z => String(z.dept))),
  ])].filter(Boolean).sort();

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Secteurs techniciens</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {etat.maj && (
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              modifié le {new Date(etat.maj.le).toLocaleString('fr-FR')} par {etat.maj.par}
            </span>
          )}
          {ok && <span style={{ fontSize: 13, color: 'var(--green2)', fontWeight: 600 }}>✓ Enregistré</span>}
          {erreur && <span style={{ fontSize: 13, color: '#ff6b6b' }}>{erreur}</span>}
          <button className="btn btn-primary btn-sm" onClick={enregistrer} disabled={sauvegarde}>
            {sauvegarde ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      <div className="page">
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 18, lineHeight: 1.6, maxWidth: 820 }}>
          Cette table pilote l'ouverture des créneaux E-RDV chez Covage et la recommandation de rendez-vous.
          Le <strong>rang</strong> ordonne les départements d'un technicien, 1 étant sa zone principale.
          Les <strong>codes postaux</strong> ne se remplissent que si la couverture est partielle — le Var
          partagé entre deux techniciens, ou Paris découpé par arrondissements. Laissés vides, ils signifient
          « tout le département ».
        </div>

        {erreurs.length > 0 && (
          <div style={{ background: 'rgba(255,107,107,.1)', border: '1px solid rgba(255,107,107,.35)', borderRadius: 8, padding: '12px 16px', marginBottom: 18 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#ff6b6b', marginBottom: 6 }}>
              Configuration refusée — rien n'a été enregistré
            </div>
            {erreurs.map((e, i) => (
              <div key={i} style={{ fontSize: 12, color: 'var(--text2)', padding: '2px 0' }}>• {e}</div>
            ))}
          </div>
        )}

        {(deptsSansMode.length > 0 || deptsSansTech.length > 0) && (
          <div style={{ background: 'rgba(219,139,0,.1)', border: '1px solid rgba(219,139,0,.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 18 }}>
            {deptsSansMode.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                Départements couverts mais sans mode d'ouverture : <strong>{deptsSansMode.join(', ')}</strong>
                {' '}— ils ne seront pilotés par rien.
              </div>
            )}
            {deptsSansTech.length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                Départements ouverts sans technicien : <strong>{deptsSansTech.join(', ')}</strong>
                {' '}— des rendez-vous seraient promis sans personne pour les honorer.
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            value={filtre}
            onChange={e => setFiltre(e.target.value)}
            placeholder="Filtrer par technicien, nom ou département…"
            style={{
              width: 340, padding: '8px 12px', fontSize: 13,
              background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 6, color: 'var(--text)',
            }}
          />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              value={nouveau}
              onChange={e => setNouveau(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') ajouterTech(); }}
              placeholder="VALEA-D3_70"
              style={{
                width: 180, padding: '8px 12px', fontSize: 13, fontFamily: 'monospace',
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 6, color: 'var(--text)',
              }}
            />
            <button className="btn btn-sm" onClick={ajouterTech} disabled={!nouveau.trim()}>
              + technicien
            </button>
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: -10, marginBottom: 18, textAlign: 'right' }}>
          Le nom doit être celui de Zoho, tel qu'il figure dans le champ Technicien_OT.
        </div>

        {/* ── Techniciens ── */}
        <div style={{ display: 'grid', gap: 12, marginBottom: 32 }}>
          {techs.map(tech => {
            const zones = etat.techniciens[tech].zones || [];
            return (
              <div key={tech} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: zones.length ? 10 : 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{tech}</span>
                  {nomDe(tech) && <span style={{ fontSize: 12, color: 'var(--text3)' }}>{nomDe(tech)}</span>}
                  {zones.length === 0
                    ? <Pastille ton="gris">aucune zone</Pastille>
                    : <Pastille ton="vert">{zones.length} département{zones.length > 1 ? 's' : ''}</Pastille>}
                  <button className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => ajouterZone(tech)}>
                    + département
                  </button>
                  <button className="btn btn-sm" onClick={() => retirerTech(tech)}
                    title="Retirer de la couverture — le compte n'est pas supprimé"
                    style={{ color: '#ff6b6b' }}>
                    Retirer
                  </button>
                </div>

                {zones.length > 0 && (
                  <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ color: 'var(--text3)', textAlign: 'left' }}>
                        <th style={{ padding: '4px 6px', width: 60 }}>Rang</th>
                        <th style={{ padding: '4px 6px', width: 80 }}>Dept</th>
                        <th style={{ padding: '4px 6px' }}>Codes postaux (si couverture partielle)</th>
                        <th style={{ padding: '4px 6px', width: 120 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {zones.map((z, i) => (
                        <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                          <td style={{ padding: '4px 6px' }}>
                            <input
                              type="number" min="1" value={z.rang ?? ''}
                              onChange={e => modifierZone(tech, i, 'rang', e.target.value)}
                              style={{ width: 46, padding: '4px 6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)' }}
                            />
                          </td>
                          <td style={{ padding: '4px 6px' }}>
                            <input
                              value={z.dept || ''} placeholder="75"
                              onChange={e => modifierZone(tech, i, 'dept', e.target.value)}
                              style={{ width: 52, padding: '4px 6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontWeight: 700 }}
                            />
                          </td>
                          <td style={{ padding: '4px 6px' }}>
                            <input
                              value={brouillons[`${tech}|${i}`] ?? (z.codes_postaux || []).join(' ')}
                              placeholder="tout le département"
                              onChange={e => {
                                const brut = e.target.value;
                                setBrouillons(b => ({ ...b, [`${tech}|${i}`]: brut }));
                                modifierZone(tech, i, 'codes_postaux', brut);
                              }}
                              onBlur={() => setBrouillons(b => {
                                const c = { ...b }; delete c[`${tech}|${i}`]; return c;
                              })}
                              style={{ width: '100%', padding: '4px 8px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontFamily: 'monospace' }}
                            />
                          </td>
                          <td style={{ padding: '4px 6px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <button className="btn btn-sm" onClick={() => deplacerZone(tech, i, -1)} disabled={i === 0} title="Monter">↑</button>
                            <button className="btn btn-sm" onClick={() => deplacerZone(tech, i, 1)} disabled={i === zones.length - 1} title="Descendre" style={{ marginLeft: 4 }}>↓</button>
                            <button className="btn btn-sm" onClick={() => supprimerZone(tech, i)} title="Retirer" style={{ marginLeft: 4, color: '#ff6b6b' }}>✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>

        {(etat.exclus || []).length > 0 && (
          <div style={{ marginBottom: 32 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Techniciens retirés de la couverture</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10, maxWidth: 820, lineHeight: 1.6 }}>
              Ils restent dans les comptes de l'application mais ne reçoivent plus de créneau.
              Sans ce retrait explicite, ils réapparaîtraient à chaque rechargement.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {(etat.exclus || []).slice().sort((a, b) => a.localeCompare(b, 'fr', { numeric: true })).map(t => (
                <div key={t} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'var(--bg2)', border: '1px dashed var(--border)',
                  borderRadius: 6, padding: '6px 10px',
                }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)' }}>{t}</span>
                  {nomDe(t) && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{nomDe(t)}</span>}
                  <button className="btn btn-sm" onClick={() => reintegrerTech(t)}>Réintégrer</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Modes d'ouverture ── */}
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Mode d'ouverture par département</div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>
          {Object.entries(MODE_LIBELLE).map(([k, v]) => `${v.texte} — ${v.aide}`).join(' · ')}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 32 }}>
          {tousDepts.map(d => {
            const mode = etat.departements[d] || '';
            return (
              <div key={d} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: 'var(--bg2)', border: '1px solid var(--border)',
                borderRadius: 6, padding: '6px 8px',
              }}>
                <span style={{ fontSize: 13, fontWeight: 800, minWidth: 22 }}>{d}</span>
                <select
                  value={mode}
                  onChange={e => setMode(d, e.target.value)}
                  style={{ fontSize: 11, padding: '3px 4px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)' }}
                >
                  <option value="">— à définir —</option>
                  {Object.entries(MODE_LIBELLE).map(([k, v]) => <option key={k} value={k}>{v.texte}</option>)}
                </select>
              </div>
            );
          })}
        </div>

        {/* ── Rotations ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Rotations</div>
          <button className="btn btn-sm" onClick={ajouterRotation}>+ rotation</button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12, maxWidth: 820, lineHeight: 1.6 }}>
          Pour les départements à faible volume : plutôt que de subir un déplacement isolé déclenché par
          une réservation au hasard, on n'ouvre qu'un jour choisi. Un cycle de 3 avec les positions 0, 1 et 2
          donne un vendredi sur trois à chaque département, à tour de rôle.
        </div>
        {etat.rotations.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text3)', padding: '10px 0' }}>Aucune rotation définie.</div>
        ) : (
          <table style={{ fontSize: 12, borderCollapse: 'collapse', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8 }}>
            <thead>
              <tr style={{ color: 'var(--text3)', textAlign: 'left' }}>
                <th style={{ padding: '6px 10px' }}>Dept</th>
                <th style={{ padding: '6px 10px' }}>Technicien</th>
                <th style={{ padding: '6px 10px' }}>Jour</th>
                <th style={{ padding: '6px 10px' }}>Cycle</th>
                <th style={{ padding: '6px 10px' }}>Position</th>
                <th style={{ padding: '6px 10px' }}></th>
              </tr>
            </thead>
            <tbody>
              {etat.rotations.map((r, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 10px' }}>
                    <input value={r.dept || ''} placeholder="15" onChange={e => majRotation(i, 'dept', e.target.value.replace(/[^0-9AB]/gi, '').slice(0, 2))}
                      style={{ width: 46, padding: '4px 6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)', fontWeight: 700 }} />
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    <select value={r.technicien || ''} onChange={e => majRotation(i, 'technicien', e.target.value)}
                      style={{ fontSize: 12, padding: '4px 6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)' }}>
                      <option value="">—</option>
                      {Object.keys(etat.techniciens).sort((a, b) => a.localeCompare(b, 'fr', { numeric: true }))
                        .map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    <select value={r.jour || 'vendredi'} onChange={e => majRotation(i, 'jour', e.target.value)}
                      style={{ fontSize: 12, padding: '4px 6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)' }}>
                      {JOURS.map(j => <option key={j} value={j}>{j}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    <input type="number" min="1" value={r.cycle ?? 3} onChange={e => majRotation(i, 'cycle', e.target.value)}
                      style={{ width: 52, padding: '4px 6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)' }} />
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    <input type="number" min="0" value={r.position ?? 0} onChange={e => majRotation(i, 'position', e.target.value)}
                      style={{ width: 52, padding: '4px 6px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text)' }} />
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    <button className="btn btn-sm" onClick={() => supprimerRotation(i)} style={{ color: '#ff6b6b' }}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
